import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeftRight, MapPin, UserCheck, Warehouse, Loader2 } from "lucide-react";
import { errMsg } from "../../lib/apiErrors";

/**
 * PickupTargetDialog — preluare PSA / Echipament cu alegerea explicită a destinației.
 *
 * Destinații suportate:
 *  - default: locația standard (PSA → user.pickup_location ex. "SERGHEI ACASA"; alte items → clear)
 *  - location: o locație custom (ex. "GARAJ CONSTANTIN", "ACASA VICTOR")
 *  - person:   transfer direct la alt angajat (P2P)
 *
 * Props:
 *  - item: inventory item ce urmează a fi preluat
 *  - locations: list of available custom locations [{ key, label }]
 *  - persons:   list of active persons [{ id, nume, prenume, ... }]
 *  - defaultLocationLabel: ex. "SERGHEI ACASA" (din user.pickup_location)
 *  - onClose, onDone
 */
export default function PickupTargetDialog({ item, locations = [], persons = [], defaultLocationLabel, onClose, onDone }) {
  const [mode, setMode] = useState("default"); // default | location | person
  const [targetLocation, setTargetLocation] = useState("");
  const [targetPersonId, setTargetPersonId] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const todayIso = new Date().toISOString().slice(0, 10);
  const [operationDate, setOperationDate] = useState(todayIso);

  useEffect(() => {
    if (!item) return;
    setMode("default");
    setTargetLocation("");
    setTargetPersonId("");
    setNote("");
    setSearch("");
    setOperationDate(new Date().toISOString().slice(0, 10));
  }, [item?.id]);

  const sortedPersons = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (persons || [])
      .filter((p) => p.id !== item?.assignee_person_id)
      .filter((p) => !q || `${p.nume || ""} ${p.prenume || ""}`.toLowerCase().includes(q))
      .sort((a, b) => `${a.nume || ""} ${a.prenume || ""}`.localeCompare(`${b.nume || ""} ${b.prenume || ""}`, "ro"));
  }, [persons, search, item]);

  const submit = async () => {
    if (!item) return;
    if (!operationDate) {
      toast.error("Specifică data predării"); return;
    }
    if (mode === "location" && !targetLocation) {
      toast.error("Alege o locație"); return;
    }
    if (mode === "person" && !targetPersonId) {
      toast.error("Alege persoana destinație"); return;
    }
    setBusy(true);
    try {
      const payload = {
        action: "pickup",
        item_id: item.id,
        note: note.trim() || null,
        operation_date: operationDate || null,
      };
      if (mode === "location") payload.target_location = targetLocation;
      if (mode === "person") payload.target_person_id = targetPersonId;
      await api.post("/inventory/transfers", payload);
      // Mesaj clar despre unde a ajuns item-ul
      let dest = `locația ${defaultLocationLabel || "implicită"}`;
      if (mode === "location") dest = `locația ${targetLocation}`;
      if (mode === "person") {
        const p = persons.find((x) => x.id === targetPersonId);
        dest = `persoana ${p ? `${p.nume} ${p.prenume}` : ""}`;
      }
      toast.success(`${item.name} → ${dest}`);
      onDone && onDone();
    } catch (e) {
      toast.error(errMsg(e, "Eroare la preluare"));
    } finally {
      setBusy(false);
    }
  };

  if (!item) return null;

  return (
    <Dialog open onOpenChange={(v) => { if (!v && !busy) onClose && onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="pickup-target-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-cyan-700">
            <ArrowLeftRight size={18}/>
            Preia <span className="font-mono">{item.name}</span>
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            {item.assignee
              ? <>De la <strong className="text-zinc-800">„{item.assignee}"</strong>. Alege ce faci cu echipamentul:</>
              : <>Locația curentă: <strong className="text-zinc-800">{item.location || "—"}</strong>. Alege destinația:</>
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          {/* Mode selector — 3 carduri */}
          <div className="grid grid-cols-3 gap-2">
            <button
              type="button"
              onClick={() => setMode("default")}
              className={`p-3 rounded-lg border-2 transition-all text-left ${mode === "default" ? "border-cyan-500 bg-cyan-50 shadow-sm" : "border-zinc-200 bg-white hover:border-zinc-300"}`}
              data-testid="pickup-mode-default"
            >
              <Warehouse size={18} className={mode === "default" ? "text-cyan-700" : "text-zinc-500"}/>
              <div className="font-bold text-xs mt-1">Default</div>
              <div className="text-[10px] text-zinc-500 mt-0.5 line-clamp-2">
                {defaultLocationLabel || (item.item_type === "psa" ? "SERGHEI ACASA" : "fără locație")}
              </div>
            </button>
            <button
              type="button"
              onClick={() => setMode("location")}
              className={`p-3 rounded-lg border-2 transition-all text-left ${mode === "location" ? "border-cyan-500 bg-cyan-50 shadow-sm" : "border-zinc-200 bg-white hover:border-zinc-300"}`}
              data-testid="pickup-mode-location"
            >
              <MapPin size={18} className={mode === "location" ? "text-cyan-700" : "text-zinc-500"}/>
              <div className="font-bold text-xs mt-1">Locație</div>
              <div className="text-[10px] text-zinc-500 mt-0.5">Garaj, magazie etc.</div>
            </button>
            <button
              type="button"
              onClick={() => setMode("person")}
              className={`p-3 rounded-lg border-2 transition-all text-left ${mode === "person" ? "border-cyan-500 bg-cyan-50 shadow-sm" : "border-zinc-200 bg-white hover:border-zinc-300"}`}
              data-testid="pickup-mode-person"
            >
              <UserCheck size={18} className={mode === "person" ? "text-cyan-700" : "text-zinc-500"}/>
              <div className="font-bold text-xs mt-1">Persoană</div>
              <div className="text-[10px] text-zinc-500 mt-0.5">Direct la alt angajat</div>
            </button>
          </div>

          {/* Mode-specific UI */}
          {mode === "default" && (
            <div className="bg-zinc-50 border border-zinc-200 rounded-md p-3 text-xs text-zinc-700" data-testid="pickup-mode-default-desc">
              Echipamentul va merge la locația ta personalizată: <strong>{defaultLocationLabel || (item.item_type === "psa" ? "SERGHEI ACASA" : "(fără locație custom)")}</strong>.
            </div>
          )}

          {mode === "location" && (
            <div className="space-y-1.5" data-testid="pickup-mode-location-form">
              <Label className="text-[11px] uppercase tracking-wider text-zinc-500">Alege locația *</Label>
              <Select value={targetLocation} onValueChange={setTargetLocation}>
                <SelectTrigger data-testid="pickup-location-select">
                  <SelectValue placeholder="— Alege locația —"/>
                </SelectTrigger>
                <SelectContent>
                  {(locations || []).length === 0 && (
                    <div className="px-2 py-1 text-xs text-zinc-400 italic">Nicio locație custom definită</div>
                  )}
                  {(locations || []).map((loc) => (
                    <SelectItem key={loc.key || loc.label} value={loc.label}>📍 {loc.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-[10px] text-zinc-500 italic">
                Item-ul va fi mutat la locația aleasă, fără atribuire la o persoană.
              </div>
            </div>
          )}

          {mode === "person" && (
            <div className="space-y-1.5" data-testid="pickup-mode-person-form">
              <Label className="text-[11px] uppercase tracking-wider text-zinc-500">Transferă direct la *</Label>
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="🔍 Caută persoana..."
                className="h-9"
                data-testid="pickup-person-search"
              />
              <div className="max-h-[220px] overflow-y-auto border border-zinc-200 rounded divide-y" data-testid="pickup-person-list">
                {sortedPersons.length === 0 ? (
                  <div className="px-3 py-4 text-center text-xs text-zinc-400 italic">
                    Nicio persoană găsită
                  </div>
                ) : (
                  sortedPersons.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setTargetPersonId(p.id)}
                      className={`w-full text-left px-3 py-2 text-sm transition-colors ${targetPersonId === p.id ? "bg-cyan-100 font-semibold text-cyan-900" : "hover:bg-zinc-50"}`}
                      data-testid={`pickup-person-${p.id}`}
                    >
                      {targetPersonId === p.id && "✓ "}
                      {p.nume} {p.prenume}
                      {p.role && <span className="text-[10px] text-zinc-400 ml-2">· {p.role}</span>}
                    </button>
                  ))
                )}
              </div>
              <div className="text-[10px] text-zinc-500 italic">
                Item-ul va fi transferat direct (fără pas intermediar prin magazie). Persoana va primi notificare.
              </div>
            </div>
          )}

          {/* Notă + Data operațiunii (toate modurile) */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2 space-y-1">
              <Label className="text-[11px] uppercase tracking-wider text-zinc-500">Notă (opțional)</Label>
              <Textarea
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="ex: defect ușor, are nevoie de curățare"
                className="text-sm"
                data-testid="pickup-note"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px] uppercase tracking-wider text-zinc-500 flex items-center gap-1">📅 Data preluării <span className="text-red-600">*</span></Label>
              <Input
                type="date"
                value={operationDate}
                onChange={(e) => setOperationDate(e.target.value)}
                max={todayIso}
                required
                className="text-sm h-9"
                data-testid="pickup-operation-date"
              />
              <div className="text-[10px] text-zinc-500 italic">Default azi (obligatoriu)</div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Anulează</Button>
          <Button
            onClick={submit}
            disabled={busy || !operationDate || (mode === "location" && !targetLocation) || (mode === "person" && !targetPersonId)}
            className="bg-cyan-600 hover:bg-cyan-700 text-white"
            data-testid="pickup-confirm"
          >
            {busy ? <><Loader2 className="animate-spin mr-1" size={14}/>Se preia...</> : <><ArrowLeftRight size={14} className="mr-1.5"/>Confirmă preluarea</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
